import {hasAnyPermission, isSuperAdminOnly} from '../../../utils';
import {NavLink, useLocation} from 'react-router';
import useText from '../../../hooks/useText';
import {SVG} from '../../../constants/assetLists';

type Props = {
  handleMenuOpenClose?: (state: boolean) => void;
};

const getHeaderItems = (t: (key: string) => string) => [
  {
    path: '/dashboard',
    permissions: ['manage_dashboard'],
    name: t('dashboard.title'),
    icon: SVG.dashboardIcon,
    activeIcon: SVG.dashboardActiveIcon,
    disabled: true,
  },
  {
    path: '/operations',
    permissions: ['manage_alerts'],
    name: t('operations.title'),
    icon: SVG.operationsIcon,
    activeIcon: SVG.operationsActiveIcon,
    disabled: false,
  },
  {
    path: '/checklist',
    permissions: ['manage_checklists'],
    name: t('checklist.title'),
    icon: SVG.checklistIcon,
    activeIcon: SVG.checklistActiveIcon,
    disabled: false,
  },
  {
    path: '/forms',
    permissions: ['manage_forms'],
    name: t('forms.title'),
    icon: SVG.tasksIcon,
    activeIcon: SVG.tasksActiveIcon,
    disabled: false,
  },
  {
    path: '/regulations',
    permissions: ['manage_regulations'],
    name: t('regulations.title'),
    icon: SVG.regulationsIcon,
    activeIcon: SVG.regulationsActiveIcon,
    disabled: true,
  },
  {
    path: '/employees',
    permissions: ['manage_employees'],
    name: t('working.title'),
    icon: SVG.workingIcon,
    activeIcon: SVG.workingActiveIcon,
    disabled: true,
  },
  {
    path: '/equipment',
    permissions: ['manage_equipment'],
    name: t('equipmentAndInstruments.title'),
    icon: SVG.equipmentAndInstrumentsIcon,
    activeIcon: SVG.equipmentAndInstrumentsActiveIcon,
    disabled: true,
  },
  {
    path: '/users',
    permissions: ['manage_users'],
    name: t('users.title'),
    icon: SVG.usersIcon,
    activeIcon: SVG.usersActiveIcon,
    disabled: false,
  },
  {
    path: '/suppliers',
    permissions: ['manage_suppliers'],
    name: t('suppliers.title'),
    icon: SVG.suppliersIcon,
    activeIcon: SVG.suppliersActiveIcon,
    disabled: true,
  },
  {
    path: '/organizations',
    permissions: ['manage_organization'],
    name: t('organizations.title'),
    icon: SVG.organizationGrayIcon,
    activeIcon: SVG.clientIcon,
    disabled: false,
    superAdminOnly: true,
  },
  {
    path: '/tutorials',
    permissions: ['manage_tutorials'],
    name: t('tutorials.title'),
    icon: SVG.tutorialsIcon,
    activeIcon: SVG.tutorialsActiveIcon,
    disabled: true,
  },
  {
    path: '/reports',
    permissions: ['manage_reports'],
    name: t('reports.title'),
    icon: SVG.reportsIcon,
    activeIcon: SVG.reportsActiveIcon,
    disabled: true,
  },
];

function HeaderMenu({handleMenuOpenClose}: Props) {
  const t = useText();

  const location = useLocation();

  const HeaderItems = getHeaderItems(t);

  const renderNavItem = (item: any, index: number) => {
    const isActive = item.path === location.pathname;
    const permissionGranted =
      (!item.superAdminOnly || isSuperAdminOnly()) &&
      hasAnyPermission(item.permissions);

    return (
      permissionGranted && (
        <NavLink
          key={index}
          to={item.path}
          onClick={() => handleMenuOpenClose?.(false)}
          className={`flex-shrink-0 w-full lg:w-[102px] px-4 py-[6px] flex lg:flex-col lg:justify-center items-center extraSmall:gap-4 sm:gap-4 md:gap-4 lg:gap-1 rounded-[16px] text-decoration-none transition-all duration-200
            hover:bg-gradient-to-b hover:from-[#84FFE1] hover:to-[#96F3FF]
            group/link
            ${isActive ? 'bg-gradient-to-b from-[#52FFE5] to-[#69EEFFFA]' : ''} 
            ${item?.disabled && 'pointer-events-none opacity-40'}`}>
          {/* hover state */}
          <img
            src={item.activeIcon}
            alt="icon"
            className="w-[24px] h-[24px] object-contain hidden group-hover/link:block"
          />
          <span
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 text-black hidden group-hover/link:block`}>
            {item.name}
          </span>

          {/* normal state */}
          <img
            src={isActive ? item.activeIcon : item.icon}
            alt="icon"
            className="w-[24px] h-[24px] object-contain group-hover/link:hidden"
          />
          <span
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 group-hover/link:hidden ${
              isActive ? 'text-black' : 'text-gray50'
            }`}>
            {item.name}
          </span>
        </NavLink>
      )
    );
  };

  return (
    <div className="flex-1 flex flex-col lg:flex-row lg:justify-between items-center gap-2 min-w-0 min-h-0">
      <div className="w-[150px] lg:w-[1px] h-[1px] lg:h-[50px] bg-[#707070]" />

      <div className="w-full flex-1 overflow-auto lg:px-2 lg:py-2 no-scrollbar">
        <div className="flex flex-col lg:flex-row flex-nowrap items-center gap-2">
          {HeaderItems.map((item, index) => renderNavItem(item, index))}
        </div>
      </div>

      <div className="w-[150px] lg:w-[1px] h-[1px] lg:h-[50px] bg-[#707070]" />
    </div>
  );
}

export default HeaderMenu;
